import { useForm } from 'react-hook-form';
import { useRegisterUser } from '@edison/api-client-react';
import { AlertCircle } from 'lucide-react';

interface FormValues {
  tenantName: string;
}

interface Props {
  onRegistered: () => void;
}

export function RegisterPage({ onRegistered }: Props) {
  const { mutate: register, isPending, error } = useRegisterUser();

  const { register: reg, handleSubmit, formState: { errors } } = useForm<FormValues>();

  const onSubmit = (values: FormValues) => {
    register({ tenantName: values.tenantName }, { onSuccess: () => onRegistered() });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="w-full max-w-md space-y-6">
        <div className="text-center">
          <h1 className="text-2xl font-semibold tracking-tight">Welcome to Edison</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Set up your organisation to start capturing R&D evidence.
          </p>
        </div>

        {error && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-3 flex items-center gap-2 text-destructive text-sm">
            <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span>Registration failed. Please try again.</span>
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 rounded-lg border p-6">
          <div className="space-y-2">
            <label htmlFor="tenantName" className="block text-sm font-medium">
              Organisation name <span className="text-destructive">*</span>
            </label>
            <input
              id="tenantName"
              type="text"
              placeholder="e.g. Polus AI"
              className="w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              {...reg('tenantName', { required: 'Organisation name is required' })}
            />
            {errors.tenantName && (
              <p className="text-xs text-destructive">{errors.tenantName.message}</p>
            )}
          </div>

          <button
            type="submit"
            disabled={isPending}
            className="w-full rounded-lg bg-primary py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isPending ? 'Creating organisation…' : 'Get started'}
          </button>
        </form>
      </div>
    </div>
  );
}
